import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { ShoppingBag, Search, Menu, X, User, LogOut, LogIn, ChevronDown, ShieldCheck, Lock, FlaskConical, BadgeCheck, Home, Store } from "lucide-react";
import ShopProductsMenu from "@/components/ShopProductsMenu";

export default function Layout({ children }) {
  const { user, logout, cart } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [cats, setCats] = useState([]);
  const [menuRows, setMenuRows] = useState([]);
  const [searchOpen, setSearchOpen] = useState(false);
  const [q, setQ] = useState("");

  useEffect(() => { api.get("/categories").then((r) => setCats(r.data || [])).catch(() => {}); }, []);
  useEffect(() => { api.get("/menu").then((r) => setMenuRows(r.data || [])).catch(() => setMenuRows([])); }, []);

  const cartCount = cart.reduce((a, b) => a + b.quantity, 0);

  const onSearch = (e) => {
    e.preventDefault();
    if (q.trim()) navigate(`/shop?q=${encodeURIComponent(q.trim())}`);
    setSearchOpen(false);
  };

  return (
    <div className="min-h-screen flex flex-col">
      {/* Top RUO ribbon */}
      <div className="bg-[var(--paper-3)] text-[var(--ink)] py-2 overflow-hidden border-b border-[var(--line)]" data-testid="ruo-ribbon">
        <div className="marquee">
          <div className="marquee-track">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="font-mono text-[10.5px] tracking-[0.22em] uppercase flex items-center gap-3 shrink-0">
                <span className="inline-block h-1.5 w-1.5 rounded-full bg-[var(--terra)] shadow-[0_0_8px_rgba(63,255,224,0.8)]" />
                <span className="text-[var(--ink-soft)]">Research Use Only · Not For Human Consumption</span>
                <span className="inline-block h-1.5 w-1.5 rounded-full bg-[var(--magenta)] shadow-[0_0_8px_rgba(255,79,216,0.8)]" />
                <span className="text-[var(--ink-soft)]">21+ Verified Buyers</span>
                <span className="inline-block h-1.5 w-1.5 rounded-full bg-[var(--terra)] shadow-[0_0_8px_rgba(63,255,224,0.8)]" />
                <span className="text-[var(--ink-soft)]">Per-Batch COA on Every Order</span>
                <span className="inline-block h-1.5 w-1.5 rounded-full bg-[var(--violet)] shadow-[0_0_8px_rgba(138,107,255,0.8)]" />
                <span className="text-[var(--ink-soft)]">2-Day Shipping · USPS · FedEx · UPS</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Main header */}
      <header className="bg-[var(--paper)]/80 border-b border-[var(--line)] sticky top-0 z-40 backdrop-blur-xl">
        <div className="max-w-screen-2xl mx-auto px-3 sm:px-6 h-16 lg:h-20 flex items-center justify-between gap-2 sm:gap-4">
          {/* Hamburger — deliberately prominent on mobile so it can't be missed. */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="lg:hidden inline-flex items-center gap-1.5 h-10 px-3 rounded-full border border-[var(--line)] bg-[var(--paper-2)] hover:bg-[var(--paper-3)] active:scale-95 transition"
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
            data-testid="mobile-menu-btn"
          >
            {mobileOpen ? <X className="h-5 w-5" strokeWidth={2} /> : <Menu className="h-5 w-5" strokeWidth={2} />}
            <span className="font-mono text-[10.5px] uppercase tracking-[0.16em] hidden xs:inline">Menu</span>
          </button>

          <Link to="/" className="flex items-center gap-2 sm:gap-3" data-testid="logo-link">
            <div className="relative h-9 w-9 lg:h-10 lg:w-10 shrink-0">
              <div className="absolute inset-0 rounded-full bg-gradient-to-br from-[var(--terra)] via-[var(--violet)] to-[var(--magenta)] pulse-glow" />
              <div className="absolute inset-[3px] rounded-full bg-[var(--paper)] grid place-items-center overflow-hidden">
                <div className="absolute inset-0 starfield opacity-80" />
                <span className="relative font-display font-bold text-[var(--terra)] text-[14px] lg:text-[15px] glow-cyan">N</span>
              </div>
            </div>
            <div className="hidden sm:block min-w-0">
              <div className="font-display font-bold text-[16px] lg:text-[18px] tracking-[0.04em] leading-none text-[var(--ink)] truncate">NEBULA AMINOS</div>
              <div className="font-mono text-[9.5px] tracking-[0.22em] uppercase text-[var(--terra)] mt-1 truncate">Reference-Grade · Batch-Verified</div>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-6 text-[14px] font-medium" data-testid="primary-nav">
            <ShopProductsMenu />
            <span className="h-4 w-px bg-[var(--line)]" />
            <Link to="/coa" className="link-line py-1" data-testid="nav-coa">COA Hub</Link>
            <Link to="/library" className="link-line py-1" data-testid="nav-library">Research</Link>
            <Link to="/calculator" className="link-line py-1" data-testid="nav-calculator">Calculator</Link>
          </nav>

          {/* Always-visible inline search on desktop.  Compact and can be
              submitted with Enter; the mobile version lives inside the
              mobile drawer below. */}
          <form
            onSubmit={onSearch}
            className="hidden md:flex items-center gap-2 mx-4 flex-1 max-w-sm bg-[var(--paper-2)] border border-[var(--line)] rounded-full px-3.5 py-1.5 focus-within:border-[var(--terra)] transition-colors"
            data-testid="header-search-form"
          >
            <Search className="h-4 w-4 text-[var(--ink-muted)] shrink-0" strokeWidth={1.6} />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search compounds, peptides, lab supplies…"
              className="flex-1 bg-transparent outline-none text-sm placeholder:text-[var(--ink-muted)]"
              data-testid="search-input"
            />
          </form>

          <div className="flex items-center gap-1 sm:gap-2">
            {/* Mobile search icon — opens the same expanded bar for small screens */}
            <button
              onClick={() => setSearchOpen(!searchOpen)}
              className="md:hidden p-2 hover:bg-[var(--paper-2)] rounded-full transition-colors"
              aria-label="Search"
              data-testid="search-btn"
            >
              <Search className="h-5 w-5" strokeWidth={1.6} />
            </button>
            {user && user !== false ? (
              <div className="hidden sm:flex items-center gap-1">
                {user.role === "admin" && (
                  <Link to="/admin" className="btn-pill" data-testid="admin-pill">Admin</Link>
                )}
                <button onClick={() => navigate("/account")} className="p-2 hover:bg-[var(--paper-2)] rounded-full" data-testid="account-btn" aria-label="Account">
                  <User className="h-5 w-5" strokeWidth={1.6} />
                </button>
                <button onClick={logout} className="p-2 hover:bg-[var(--paper-2)] rounded-full" data-testid="logout-btn" aria-label="Logout">
                  <LogOut className="h-5 w-5" strokeWidth={1.6} />
                </button>
              </div>
            ) : (
              // Persistent Sign-in pill for returning buyers. Prominent enough
              // that it can't be missed but doesn't compete with the primary
              // shopping CTAs. Icon-only on very narrow screens, full pill on sm+.
              <Link
                to="/login"
                className="hidden sm:inline-flex items-center gap-1.5 h-9 px-3.5 rounded-full border border-[var(--line)] bg-[var(--paper-2)] hover:border-[var(--terra)] hover:text-[var(--terra)] active:scale-95 transition text-[13px] font-medium"
                data-testid="header-signin-pill"
              >
                <LogIn className="h-4 w-4" strokeWidth={1.7} />
                <span>Sign in</span>
              </Link>
            )}
            <Link to="/checkout" className="relative p-2 hover:bg-[var(--paper-2)] rounded-full" data-testid="cart-btn" aria-label="Cart">
              <ShoppingBag className="h-5 w-5" strokeWidth={1.6} />
              {cartCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 bg-[var(--terra)] text-[var(--paper)] text-[10px] font-mono font-semibold h-5 min-w-5 px-1 grid place-items-center rounded-full">
                  {cartCount}
                </span>
              )}
            </Link>
          </div>
        </div>

        {/* Search expanded */}
        {searchOpen && (
          <div className="border-t border-[var(--line)] bg-[var(--paper)]">
            <form onSubmit={onSearch} className="max-w-screen-2xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-3">
              <Search className="h-5 w-5 text-[var(--ink-muted)]" />
              <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search compounds, peptides, lab supplies…" className="flex-1 bg-transparent outline-none text-base placeholder:text-[var(--ink-muted)]" data-testid="search-input" />
              <button type="submit" className="btn-primary text-sm" data-testid="search-submit">Search</button>
            </form>
          </div>
        )}

        {/* Mobile nav */}
        {mobileOpen && (
          <div className="lg:hidden border-t border-[var(--line)] bg-[var(--paper)] max-h-[calc(100vh-5rem)] overflow-y-auto" data-testid="mobile-nav">
            <div className="px-6 py-4 flex flex-col gap-3 text-sm">
              {/* Signed-in identity strip — mirrors the desktop account/logout icons */}
              {user && user !== false ? (
                <div className="pb-3 mb-1 border-b border-[var(--line)]" data-testid="mobile-user-strip">
                  <div className="flex items-center gap-3">
                    <div className="h-9 w-9 rounded-full bg-[var(--paper-2)] grid place-items-center">
                      <User className="h-4 w-4" strokeWidth={1.6} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-[13px] font-medium truncate">{user.name || user.email}</div>
                      <div className="text-[11px] font-mono uppercase tracking-wider text-[var(--ink-muted)] truncate">{user.email}</div>
                    </div>
                    {user.role === "admin" && (
                      <Link
                        to="/admin"
                        onClick={() => setMobileOpen(false)}
                        className="btn-pill shrink-0"
                        data-testid="mobile-admin-pill"
                      >
                        Admin
                      </Link>
                    )}
                  </div>
                  <div className="mt-3 grid grid-cols-2 gap-2 text-[12px] font-mono uppercase tracking-wider">
                    <Link to="/account" onClick={() => setMobileOpen(false)} className="px-3 py-2 border border-[var(--line)] rounded-md hover:border-[var(--terra)]" data-testid="mobile-account-link">
                      Account
                    </Link>
                    <Link to="/account/orders" onClick={() => setMobileOpen(false)} className="px-3 py-2 border border-[var(--line)] rounded-md hover:border-[var(--terra)]" data-testid="mobile-orders-link">
                      Orders
                    </Link>
                    <Link to="/account/addresses" onClick={() => setMobileOpen(false)} className="px-3 py-2 border border-[var(--line)] rounded-md hover:border-[var(--terra)]" data-testid="mobile-addresses-link">
                      Addresses
                    </Link>
                    <Link to="/account/invoices" onClick={() => setMobileOpen(false)} className="px-3 py-2 border border-[var(--line)] rounded-md hover:border-[var(--terra)]" data-testid="mobile-invoices-link">
                      Invoices
                    </Link>
                  </div>
                  <button
                    onClick={() => { setMobileOpen(false); logout(); }}
                    className="mt-3 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 border border-[var(--crimson)]/40 text-[var(--crimson)] font-mono text-[12px] uppercase tracking-[0.16em] rounded-md hover:bg-[var(--crimson)]/10"
                    data-testid="mobile-logout-btn"
                  >
                    <LogOut className="h-4 w-4" /> Sign out
                  </button>
                </div>
              ) : null}

              <Link to="/shop" onClick={() => setMobileOpen(false)} className="font-medium">Shop All</Link>
              <Link to="/coa" onClick={() => setMobileOpen(false)} data-testid="mobile-nav-coa">COA Hub</Link>
              <Link to="/library" onClick={() => setMobileOpen(false)} data-testid="mobile-nav-library">Research Library</Link>
              <Link to="/calculator" onClick={() => setMobileOpen(false)} data-testid="mobile-nav-calculator">Peptide Calculator</Link>
              <Link to="/quality" onClick={() => setMobileOpen(false)} data-testid="mobile-nav-quality">Quality &amp; Testing</Link>
              <Link to="/compliance" onClick={() => setMobileOpen(false)} data-testid="mobile-nav-compliance">Compliance</Link>

              <div className="pl-3 border-l border-[var(--line)] flex flex-col gap-2 mt-1">
                <div className="font-mono text-[10.5px] uppercase tracking-[0.18em] text-[var(--ink-muted)]">Featured products</div>
                {menuRows.map((p) => (
                  <Link
                    key={`m-${p.sku}-${p.variant}`}
                    to={`/shop/${p.sku}?variant=${encodeURIComponent(p.variant)}`}
                    onClick={() => setMobileOpen(false)}
                    className={`text-[13px] flex items-baseline justify-between gap-3 ${p.in_stock ? "text-[var(--ink-soft)]" : "text-[var(--ink-muted)] opacity-70"}`}
                    data-testid={`mobile-shop-${p.sku}-${p.variant.replace(/\s+/g, "")}`}
                  >
                    <span>{p.product_name}</span>
                    <span className="font-mono text-[11px] text-[var(--ink-muted)]">
                      {p.variant} · ${p.price.toFixed(p.price % 1 === 0 ? 0 : 2)} · {p.in_stock ? `${p.stock_qty} in stock` : "out"}
                    </span>
                  </Link>
                ))}
              </div>

              {!user || user === false ? (
                <Link to="/login" onClick={() => setMobileOpen(false)} className="btn-primary mt-3 justify-center" data-testid="mobile-signin">
                  Sign in
                </Link>
              ) : null}
            </div>
          </div>
        )}
      </header>

      {/* Free-shipping progress strip — subtotal ≥ $199.99 waives shipping.
          Kept above the main content so buyers see it on every page. */}
      <ShippingProgress cart={cart} />

      <main className="flex-1 pb-20 lg:pb-0">{children}</main>

      {/* Mobile bottom nav — always accessible, one-tap for the 5 core actions.
          Hidden on lg+ where the top nav covers the same ground. */}
      <MobileTabBar user={user} cartCount={cartCount} />

      <footer className="bg-[var(--paper-2)] text-[var(--ink)] mt-24 border-t border-[var(--line)] relative overflow-hidden">
        <div className="absolute inset-0 starfield opacity-30 pointer-events-none" />
        <div className="nebula-blob cyan absolute" style={{ width: 400, height: 400, top: -100, left: '20%', opacity: 0.12 }} />
        <div className="nebula-blob magenta absolute" style={{ width: 360, height: 360, bottom: -120, right: '10%', opacity: 0.1 }} />
        <div className="relative max-w-screen-2xl mx-auto px-4 sm:px-6 py-16 grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <div className="flex items-center gap-3">
              <div className="relative h-10 w-10">
                <div className="absolute inset-0 rounded-full bg-gradient-to-br from-[var(--terra)] via-[var(--violet)] to-[var(--magenta)]" />
                <div className="absolute inset-[3px] rounded-full bg-[var(--paper)] grid place-items-center overflow-hidden">
                  <div className="absolute inset-0 starfield" />
                  <span className="relative font-display font-bold text-[var(--terra)] text-[15px]">N</span>
                </div>
              </div>
              <div className="font-display font-bold text-xl tracking-[0.04em]">NEBULA AMINOS</div>
            </div>
            <p className="mt-5 text-sm text-[var(--ink-soft)] leading-relaxed max-w-md">
              Supplier of reference-grade research compounds to accredited laboratories and qualified 21+ researchers. Batch-specific COAs, independent third-party identity testing, free shipping on orders over $199.
            </p>
            <div className="mt-6 space-y-1.5 text-sm" data-testid="footer-contact">
              <a href="mailto:support@nebulaaminos.com" className="block text-[var(--ink-soft)] hover:text-[var(--terra)]" data-testid="footer-contact-email">support@nebulaaminos.com</a>
              <a href="tel:+13058777739" className="block text-[var(--ink-soft)] hover:text-[var(--terra)]" data-testid="footer-contact-phone">305-877-7739</a>
              <div className="text-[var(--ink-soft)]" data-testid="footer-contact-address">113 W Ryker Ln, Midvale UT 84047</div>
            </div>
            <div className="mt-5 inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.18em] text-[var(--terra)]">
              <ShieldCheck className="h-3.5 w-3.5" /> Koveralabs-verified · per-batch
            </div>
          </div>
          <div className="md:col-span-2">
            <div className="font-mono text-[10.5px] uppercase tracking-[0.22em] text-[var(--terra)] mb-4">Shop</div>
            <ul className="space-y-2 text-sm">
              <li><Link to="/shop?cat=metabolic-research-peptides" className="hover:text-[var(--terra)] text-[var(--ink-soft)]">Metabolic Research Peptides</Link></li>
              <li><Link to="/shop?cat=research-peptides" className="hover:text-[var(--terra)] text-[var(--ink-soft)]">Research Peptides</Link></li>
              <li><Link to="/shop?cat=lab-supplies" className="hover:text-[var(--terra)] text-[var(--ink-soft)]">Lab Supplies</Link></li>
            </ul>
          </div>
          <div className="md:col-span-2">
            <div className="font-mono text-[10.5px] uppercase tracking-[0.22em] text-[var(--terra)] mb-4">Quality</div>
            <ul className="space-y-2 text-sm">
              <li><Link to="/coa" className="hover:text-[var(--terra)] text-[var(--ink-soft)]">COA Hub</Link></li>
              <li><Link to="/library" className="hover:text-[var(--terra)] text-[var(--ink-soft)]">Research Library</Link></li>
              <li><Link to="/quality" className="hover:text-[var(--terra)] text-[var(--ink-soft)]">Quality &amp; Testing</Link></li>
              <li><Link to="/compliance" className="hover:text-[var(--terra)] text-[var(--ink-soft)]">Compliance</Link></li>
            </ul>
          </div>
          <div className="md:col-span-2">
            <div className="font-mono text-[10.5px] uppercase tracking-[0.22em] text-[var(--terra)] mb-4">Legal</div>
            <ul className="space-y-2 text-sm" data-testid="footer-legal">
              <li><Link to="/terms" className="hover:text-[var(--terra)] text-[var(--ink-soft)]" data-testid="footer-link-terms">Terms &amp; Conditions</Link></li>
              <li><Link to="/privacy" className="hover:text-[var(--terra)] text-[var(--ink-soft)]" data-testid="footer-link-privacy">Privacy Policy</Link></li>
              <li><Link to="/refund-policy" className="hover:text-[var(--terra)] text-[var(--ink-soft)]" data-testid="footer-link-refund">Return &amp; Refund Policy</Link></li>
              <li><Link to="/shipping-policy" className="hover:text-[var(--terra)] text-[var(--ink-soft)]" data-testid="footer-link-shipping">Shipping Policy</Link></li>
            </ul>
          </div>
          <div className="md:col-span-2">
            <div className="font-mono text-[10.5px] uppercase tracking-[0.22em] text-[var(--terra)] mb-4">Notice</div>
            <p className="text-xs text-[var(--ink-soft)] leading-relaxed">
              All products sold by Nebula Aminos LLC are intended strictly for laboratory and in-vitro research purposes only. They are not intended for human or animal consumption, ingestion, injection, or any therapeutic, diagnostic, cosmetic, or clinical use. Products are not drugs, foods, dietary supplements, or medical devices, and have not been evaluated or approved by the FDA. By purchasing, the buyer certifies they are a qualified researcher or institution, are at least 18 years of age, and will handle all products in accordance with applicable laws and good laboratory practice. Misuse is strictly prohibited and is the sole responsibility of the purchaser.
            </p>
          </div>
        </div>
        {/* Trust / Compliance strip — sits between the columns and the copyright.
            Four minimal badges reassuring first-time buyers that this is an
            RUO-compliant, encrypted, per-batch-tested, age-gated site. */}
        <div className="relative border-t border-[var(--line)]">
          <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 py-6 grid grid-cols-2 md:grid-cols-4 gap-4">
            <TrustBadge
              icon={<Lock className="h-4 w-4" />}
              label="SSL Encrypted"
              detail="TLS 1.3 · checkout hardened"
              testid="footer-badge-ssl"
            />
            <TrustBadge
              icon={<FlaskConical className="h-4 w-4" />}
              label="Per-Batch COA"
              detail="Koveralabs · public verify"
              testid="footer-badge-coa"
              to="/coa"
            />
            <TrustBadge
              icon={<ShieldCheck className="h-4 w-4" />}
              label="RUO Compliant"
              detail="Research use only · RUO"
              testid="footer-badge-ruo"
              to="/compliance"
            />
            <TrustBadge
              icon={<BadgeCheck className="h-4 w-4" />}
              label="21+ Verified Buyers"
              detail="Age gate · account required"
              testid="footer-badge-age"
            />
          </div>
        </div>
        <div className="relative border-t border-[var(--line)] py-5">
          <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 flex flex-col md:flex-row items-center justify-between gap-2 text-[11px] font-mono uppercase tracking-[0.18em] text-[var(--ink-muted)]">
            <div>© 2026 Nebula Aminos · All rights reserved</div>
            <div>Research Use Only · 21+ Verified · Per-Batch COA</div>
          </div>
        </div>
      </footer>
    </div>
  );
}

/**
 * Fixed bottom quick-nav for phones — the five most important destinations
 * always one tap away. Hidden on lg+ where the desktop header handles nav.
 */
function MobileTabBar({ user, cartCount }) {
  const items = [
    { to: "/", icon: Home, label: "Home", test: "tab-home" },
    { to: "/shop", icon: Store, label: "Shop", test: "tab-shop" },
    { to: "/calculator", icon: FlaskConical, label: "Calc", test: "tab-calc" },
    { to: "/checkout", icon: ShoppingBag, label: "Cart", test: "tab-cart", badge: cartCount },
    { to: user ? "/account" : "/login", icon: User, label: user ? "Me" : "Sign in", test: "tab-account" },
  ];
  return (
    <nav
      className="lg:hidden fixed bottom-0 inset-x-0 z-40 border-t border-[var(--line)] bg-[var(--paper)]/95 backdrop-blur-lg pb-[env(safe-area-inset-bottom)]"
      data-testid="mobile-tabbar"
      aria-label="Mobile quick navigation"
    >
      <div className="grid grid-cols-5">
        {items.map((it) => {
          const Icon = it.icon;
          return (
            <Link
              key={it.to}
              to={it.to}
              className="flex flex-col items-center justify-center py-2 gap-0.5 text-[var(--ink-soft)] hover:text-[var(--terra)] active:bg-[var(--paper-2)] transition-colors relative"
              data-testid={it.test}
            >
              <div className="relative">
                <Icon className="h-5 w-5" strokeWidth={1.6} />
                {it.badge > 0 && (
                  <span className="absolute -top-1.5 -right-2 bg-[var(--terra)] text-[var(--paper)] text-[9px] font-mono font-semibold h-4 min-w-4 px-1 grid place-items-center rounded-full">
                    {it.badge}
                  </span>
                )}
              </div>
              <span className="font-mono text-[9.5px] uppercase tracking-[0.14em]">{it.label}</span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
}



function TrustBadge({ icon, label, detail, testid, to }) {
  const body = (
    <div
      className="group flex items-center gap-3 rounded-lg border border-[var(--line)] bg-[var(--paper)]/60 backdrop-blur-sm px-4 py-3 hover:border-[var(--terra)]/70 transition-colors"
      data-testid={testid}
    >
      <div className="grid place-items-center h-8 w-8 rounded-md bg-[var(--terra)]/10 text-[var(--terra)] shrink-0 group-hover:bg-[var(--terra)]/20 transition-colors">
        {icon}
      </div>
      <div className="min-w-0">
        <div className="text-[11px] font-mono uppercase tracking-[0.16em] text-[var(--ink)] truncate">{label}</div>
        <div className="text-[10.5px] text-[var(--ink-muted)] truncate">{detail}</div>
      </div>
    </div>
  );
  if (to) {
    return (
      <Link to={to} className="block">
        {body}
      </Link>
    );
  }
  return body;
}



function ShippingProgress({ cart }) {
  if (!cart || cart.length === 0) return null;
  const subtotal = cart.reduce((s, i) => s + (Number(i.price) || 0) * (Number(i.quantity) || 0), 0);
  if (subtotal <= 0) return null;
  const THRESHOLD = 199.99;
  const remaining = THRESHOLD - subtotal;
  const pct = Math.min(100, (subtotal / THRESHOLD) * 100);
  const qualifies = remaining <= 0;
  return (
    <div className="sticky top-0 z-30 border-b border-[var(--line)] bg-[var(--paper-2)]/95 backdrop-blur-md" data-testid="shipping-progress">
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 py-2.5 flex items-center gap-4">
        <div className="flex-1 flex items-center gap-3 min-w-0">
          <div className="font-mono text-[10.5px] tracking-[0.18em] uppercase text-[var(--ink-muted)] shrink-0">
            Cart · ${subtotal.toFixed(2)}
          </div>
          <div className="flex-1 h-1.5 rounded-full bg-[var(--paper-3)] overflow-hidden min-w-[120px]">
            <div
              className="h-full rounded-full transition-all duration-700"
              style={{
                width: `${pct}%`,
                background: qualifies
                  ? "linear-gradient(90deg, var(--terra), var(--olive))"
                  : "linear-gradient(90deg, var(--terra), var(--violet))",
                boxShadow: "0 0 12px rgba(63,255,224,0.45)",
              }}
            />
          </div>
          <div className={`font-mono text-[11px] tracking-[0.12em] uppercase shrink-0 ${qualifies ? "text-[var(--olive)]" : "text-[var(--terra)]"}`} data-testid="shipping-progress-msg">
            {qualifies ? "Free shipping unlocked ✓" : `Add $${remaining.toFixed(2)} to get free shipping`}
          </div>
        </div>
        <Link to="/checkout" className="hidden sm:inline-block text-[11px] font-mono tracking-[0.16em] uppercase text-[var(--ink-soft)] hover:text-[var(--terra)]" data-testid="shipping-progress-checkout">
          Checkout →
        </Link>
      </div>
    </div>
  );
}
